import React, {useMemo, useState} from "react";
import {format} from "date-fns";
import {QueryClient, QueryClientProvider} from "react-query";
import {Box, Divider, Grid, Stack, Typography, useTheme} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import {
    ArrowDownward,
    ArrowUpward,
    OfflineBoltOutlined,
    SimCardDownloadOutlined,
    SortByAlphaOutlined,
    SwapVert
} from "@mui/icons-material";
import {ACard, AChartX, AToggleButtonGroup, Skeleton} from "@atiautomacao/ati-ui-library";
import {RootState} from "../../../Config/Store";
import {useAppSelector} from "../../../Config/Hooks";
import {AUTHORITIES, GATEWAY_URL} from "../../../Config/Constants";
import {hasPermission} from "../../../Shared/Auth/AuthenticationUtil";
import DataNotFound from "../../../Shared/Components/DataNotFoundMessage";
import {downloadFile} from "../../../Utils/DownloadUtils";
import {
    buildDailyGenerationParams,
    DAILY_GENERATION_BY_POWER_STATION_CSV_URL,
    DailyGenerationByPowerStationReactQuery
} from "./reactQuery/DailyGenerationByPowerStationReactQuery";
import {
    buildDailyGenerationDataZoom,
    buildDailyGenerationSeries,
    calculateEnergyAxisMax,
    calculatePercentAxisMax,
    DATA_ZOOM_SLIDER_BOTTOM,
    DATA_ZOOM_SLIDER_HEIGHT,
    dailyGenerationTooltipFormatter,
    DailyGenerationSortCriterion,
    DailyGenerationSortDirection,
    DEFAULT_SORT_CRITERION,
    DEFAULT_SORT_DIRECTION,
    ENERGY_UNIT,
    formatPowerStationAxisLabel,
    PERCENT_UNIT,
    sortDailyGeneration
} from "./utils/DailyGenerationChart.utils";

const CHART_HEIGHT = 330;
const AXIS_SPLITS = 4;

const SORT_CRITERION_OPTIONS = [
    {value: "performanceRatio", label: "PR"},
    {value: "generatedEnergy", label: "Energia gerada"},
    {value: "powerStationName", label: "A-Z"},
];

const SORT_DIRECTION_OPTIONS = [
    {value: "asc", label: <><ArrowUpward sx={{fontSize: 16, marginRight: '4px'}}/>Crescente</>, ariaLabel: "Crescente"},
    {value: "desc", label: <><ArrowDownward sx={{fontSize: 16, marginRight: '4px'}}/>Decrescente</>, ariaLabel: "Decrescente"},
];

const TOGGLE_STYLE = {
    toggleButtonGroup: {paddingLeft: '4px', height: '30px'},
    toggleButton: {height: '30px'},
    typography: {fontSize: '14px'},
};

// Visão Diária: energias (barras sobrepostas) e PR (linhas) do dia por usina. SGD-3816.
// A Visão Diária não tem QueryClientProvider (os outros cards usam Redux); o card fornece o próprio.
interface GenerationEnergyProps {
    // Ordenação controlada pela página (para o grid seguir a mesma regra). Sem props, o card controla sozinho.
    sortCriterion?: DailyGenerationSortCriterion;
    sortDirection?: DailyGenerationSortDirection;
    onSortChange?: (criterion: DailyGenerationSortCriterion, direction: DailyGenerationSortDirection) => void;
    // Filtro de usinas da Visão Diária (mesmo do grid). undefined = todas; [] = nenhuma.
    powerStationIds?: number[];
    // false enquanto o filtro de usinas ainda não carregou.
    enabled?: boolean;
}

export function GenerationEnergy(props: GenerationEnergyProps) {
    const [queryClient] = useState(() => new QueryClient());
    return (
        <QueryClientProvider client={queryClient}>
            <GenerationEnergyContent {...props}/>
        </QueryClientProvider>
    );
}

function GenerationEnergyContent({sortCriterion: controlledCriterion, sortDirection: controlledDirection, onSortChange, powerStationIds, enabled = true}: GenerationEnergyProps) {
    const theme = useTheme();
    const account = useAppSelector(state => state.authentication.account);
    const userConfig = useAppSelector((state: RootState) => state.userConfigurarion);
    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);
    const isAuthorizedToSearchByCsv = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.VIEW_CSV_EXPORT, AUTHORITIES.VIEW_ENERGY_GENERATION_EXPORT, AUTHORITIES.CSV_ENERGY_GENERATION_EXPORT
    ]);

    const [internalCriterion, setInternalCriterion] = useState<DailyGenerationSortCriterion>(DEFAULT_SORT_CRITERION);
    const [internalDirection, setInternalDirection] = useState<DailyGenerationSortDirection>(DEFAULT_SORT_DIRECTION);
    const sortCriterion = controlledCriterion ?? internalCriterion;
    const sortDirection = controlledDirection ?? internalDirection;

    const changeSort = (criterion: DailyGenerationSortCriterion, direction: DailyGenerationSortDirection) => {
        setInternalCriterion(criterion);
        setInternalDirection(direction);
        onSortChange?.(criterion, direction);
    };


    const date = format(new Date(), "yyyy-MM-dd");
    const {data, isLoading} = DailyGenerationByPowerStationReactQuery(date, userConfig?.mainUpdateInterval, powerStationIds, enabled);

    const sortedData = useMemo(
        () => sortDailyGeneration(data ?? [], sortCriterion, sortDirection),
        [data, sortCriterion, sortDirection]
    );

    const option = useMemo(() => {
        const series = buildDailyGenerationSeries(sortedData);
        const energyMax = calculateEnergyAxisMax(sortedData);
        const percentMax = calculatePercentAxisMax(sortedData);
        const dataZoom = buildDailyGenerationDataZoom(sortedData.length);
        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: {type: 'shadow'},
                formatter: dailyGenerationTooltipFormatter,
            },
            toolbox: {
                show: true,
                feature: {
                    saveAsImage: {show: true, name: `energia-gerada-${date}`},
                },
            },
            legend: {
                show: true,
                type: 'scroll',
                orient: 'horizontal',
                bottom: 0,
                left: 'center',
                // As camadas de uma energia repetem o name; a legenda mostra um item por série.
                data: Array.from(new Set(series.map((serie) => serie.name))),
            },
            grid: {
                containLabel: true,
                top: 16,
                // Com a barra de zoom, o eixo X sobe para caber barra + legenda.
                bottom: dataZoom.length > 0 ? DATA_ZOOM_SLIDER_BOTTOM + DATA_ZOOM_SLIDER_HEIGHT + 16 : 40,
                right: 24,
                left: 24,
            },
            xAxis: {
                type: 'category',
                data: sortedData.map((item) => item.powerStationName),
                axisLabel: {
                    interval: 0,
                    rotate: 30,
                    formatter: formatPowerStationAxisLabel,
                },
            },
            yAxis: [
                {type: 'value', name: ENERGY_UNIT, min: 0, max: energyMax, interval: energyMax / AXIS_SPLITS},
                {
                    type: 'value',
                    name: PERCENT_UNIT,
                    min: 0,
                    max: percentMax,
                    interval: percentMax / AXIS_SPLITS,
                    axisLabel: {formatter: `{value}${PERCENT_UNIT}`},
                    splitLine: {show: false},
                },
            ],
            dataZoom,
            series,
        };
    }, [sortedData, date]);

    const handleDownloadCSV = () => {
        downloadFile(GATEWAY_URL + DAILY_GENERATION_BY_POWER_STATION_CSV_URL, buildDailyGenerationParams(date, powerStationIds), `energia-gerada-por-usina-${date}.csv`);
    };

    const handleSortCriterionChange = (_event: React.MouseEvent<HTMLElement>, value: string | null) => {
        if (value) {
            changeSort(value as DailyGenerationSortCriterion, sortDirection);
        }
    };

    const handleSortDirectionChange = (_event: React.MouseEvent<HTMLElement>, value: string | null) => {
        if (value) {
            changeSort(sortCriterion, value as DailyGenerationSortDirection);
        }
    };

    let content: JSX.Element;
    if (isLoading) {
        content = <Skeleton animation="wave" height={280} variant="rounded" width={"100%"}/>;
    } else if (sortedData.length > 0) {
        content = (
            <AChartX
                // A troca de ordenação recria o gráfico e o scroll volta ao início.
                key={`card-overview-energy-${openSubMenu}-${sortCriterion}-${sortDirection}`}
                // @ts-ignore
                option={option}
                height={CHART_HEIGHT}
                width={"100%"}
                loading={false}
                theme={theme.palette.mode}
            />
        );
    } else {
        content = <DataNotFound boxStyle={{height: 280, width: '100%'}}/>;
    }

    return (
        <ACard
            ariaLabel={"dashboard-overview-energy"}
            title="Energia Gerada"
            iconTitle={{
                showIcon: true,
                kind: "mui",
                iconMaterialComponent: OfflineBoltOutlined,
                iconTitleStyle: {color: "#2D5DE7", fontSize: 28},
            }}
            headerControlsPosition={"header"}
            headerActions={
                <Stack direction="column" spacing={"8px"}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 12px'}}>
                        <Box sx={{display: 'flex', alignItems: 'center', paddingRight: '32px'}}>
                            <SwapVert sx={{marginRight: '12px'}}/>
                            <Typography variant="body2">Ordenar por</Typography>
                        </Box>
                        <AToggleButtonGroup
                            styleProps={TOGGLE_STYLE}
                            options={SORT_CRITERION_OPTIONS}
                            value={sortCriterion}
                            exclusive
                            onChange={handleSortCriterionChange}
                        />
                    </Box>
                    <Divider/>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 12px'}}>
                        <Box sx={{display: 'flex', alignItems: 'center', paddingRight: '32px'}}>
                            <SortByAlphaOutlined sx={{marginRight: '12px'}}/>
                            <Typography variant="body2">Ordem</Typography>
                        </Box>
                        <AToggleButtonGroup
                            styleProps={TOGGLE_STYLE}
                            options={SORT_DIRECTION_OPTIONS}
                            value={sortDirection}
                            exclusive
                            onChange={handleSortDirectionChange}
                        />
                    </Box>
                    <Divider/>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 12px'}}>
                        <IconButton disabled={!isAuthorizedToSearchByCsv} size="small" sx={{borderRadius: '4px'}} onClick={handleDownloadCSV}>
                            <SimCardDownloadOutlined sx={{marginRight: '12px'}}/>
                            <Typography variant="body2">Baixar como CSV</Typography>
                        </IconButton>
                    </Box>
                    <Divider/>
                </Stack>
            }
        >
            <Grid item display={"flex"} justifyContent="center" alignItems="center" xs={12}>
                {content}
            </Grid>
        </ACard>
    );
}
